import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { adminAPI, isImpersonating, getImpersonatedUser } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, Loader2, AlertCircle, Mail, Shield, Clock, 
  Package, ArrowRight, UserCog, Eye, ShieldCheck, ShieldOff
} from 'lucide-react';
import { toast } from 'sonner';
import { ClientProfileCard } from '@/components/admin/ClientProfileCard';
import { ClientNotesCard } from '@/components/admin/ClientNotesCard';
import { authFetch } from '@/lib/auth';
import { OrderBillingBadges, BillingSummary } from '@/components/admin/ClientBilling';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Service type labels
const SERVICE_TYPE_LABELS = {
  photo_restoration: 'Photo Restoration',
  lora_training: 'LoRA Training',
  photo_scanning: 'Photo Scanning',
  digital_persona: 'Digital Persona',
  general_upload: 'General Upload',
  real_estate: 'Real Estate Photography',
  vacant_land: 'Vacant Land',
};

// Status config
const STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  pending: { label: 'Pending', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  paid: { label: 'Paid', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  completed: { label: 'Completed', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
};

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, refreshUser } = useAuth();
  
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [billing, setBilling] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  
  // Role management state
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadUserData();
    }
  }, [isAdmin, userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [userData, ordersData] = await Promise.all([
        adminAPI.getUser(userId),
        adminAPI.getUserOrders(userId),
      ]);
      
      setUser(userData);
      setOrders(ordersData);
      authFetch(`${API_BASE}/api/admin/clients/${userId}/billing`)
        .then((r) => (r.ok ? r.json() : []))
        .then((rows) => setBilling(Object.fromEntries(rows.map((b) => [b.order_id, b]))))
        .catch(() => setBilling({}));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartImpersonation = async () => {
    try {
      setImpersonating(true);
      await adminAPI.startImpersonation(userId);
      // Refresh the auth context to reflect impersonation
      await refreshUser();
      // Navigate to orders page as the impersonated user
      navigate('/orders');
    } catch (err) {
      setError(err.message);
      setImpersonating(false);
    }
  };

  const handleRoleChangeClick = (newRole) => {
    setPendingRole(newRole);
    setShowRoleDialog(true);
  };

  const handleConfirmRoleChange = async () => {
    if (!pendingRole) return;
    
    try {
      setUpdatingRole(true);
      const updatedUser = await adminAPI.updateUserRole(userId, pendingRole);
      setUser(updatedUser);
      
      toast.success('Role updated successfully', {
        description: `${updatedUser.email} is now ${pendingRole === 'admin' ? 'an Admin' : 'a Standard User'}.`,
      });
    } catch (err) {
      toast.error('Failed to update role', {
        description: err.message,
      });
    } finally {
      setUpdatingRole(false);
      setShowRoleDialog(false);
      setPendingRole(null);
    }
  };

  // Check if user is admin by looking at the roles array
  const isUserAdmin = user?.roles?.includes('admin') || false;

  // Redirect non-admins
  if (!isAdmin) {
    return <Navigate to="/orders" replace />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  // Error state
  if (error && !user) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center text-center px-4">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg text-white mb-2">Failed to load user</h3>
        <p className="text-[#E8E4DE]/60 mb-4">{error}</p>
        <Button asChild variant="outline" className="border-white/10 text-[#E8E4DE]">
          <Link to="/admin/clients">Back to Users</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Button 
            asChild
            variant="ghost" 
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/admin/clients">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Users
            </Link>
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#C6A14A]/20 rounded-full flex items-center justify-center">
                <span className="text-[#C6A14A] text-xl font-medium">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-light text-white tracking-wide" data-testid="client-header-name">
                    {user?.full_name || user?.email}
                  </h1>
                  {isUserAdmin && (
                    <Badge className="bg-[#C6A14A]/20 text-[#C6A14A] border-[#C6A14A]/30">
                      Admin
                    </Badge>
                  )}
                </div>
                <p className="text-[#E8E4DE]/60">
                  {user?.full_name ? `${user.email} · ` : ''}User ID: {user?.id.substring(0, 8)}...
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {!isUserAdmin && (
                <Button
                  onClick={handleStartImpersonation}
                  disabled={impersonating}
                  className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                  data-testid="impersonate-btn"
                >
                  {impersonating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Eye className="mr-2 h-4 w-4" />
                  )}
                  View as User
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {user && <ClientProfileCard user={user} />}
        {user && <ClientNotesCard userId={user.id} />}

        {/* User Info */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-white">Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-[#050816]/30 rounded-lg">
              <Mail className="h-5 w-5 text-[#C6A14A]" />
              <div>
                <p className="text-sm text-[#E8E4DE]/60">Email</p>
                <p className="text-white">{user?.email}</p>
              </div>
              {user?.email_verified && (
                <Badge className="ml-auto bg-green-500/20 text-green-400 border-green-500/30">
                  Verified
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-[#050816]/30 rounded-lg">
              <Shield className="h-5 w-5 text-[#C6A14A]" />
              <div className="flex-1">
                <p className="text-sm text-[#E8E4DE]/60">Role</p>
                <div className="flex items-center gap-2 mt-1">
                  {isUserAdmin ? (
                    <Badge className="bg-[#C6A14A]/20 text-[#C6A14A] border-[#C6A14A]/30">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Administrator
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[#E8E4DE]/80 border-white/20">
                      Standard User
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                {isUserAdmin ? (
                  <Button
                    onClick={() => handleRoleChangeClick('user')}
                    disabled={updatingRole}
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid="remove-admin-btn"
                  >
                    {updatingRole ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <ShieldOff className="h-4 w-4 mr-1" />
                    )}
                    Remove Admin
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleRoleChangeClick('admin')}
                    disabled={updatingRole}
                    size="sm"
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                    data-testid="make-admin-btn"
                  >
                    {updatingRole ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <ShieldCheck className="h-4 w-4 mr-1" />
                    )}
                    Make Admin
                  </Button>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-[#050816]/30 rounded-lg">
              <Clock className="h-5 w-5 text-[#C6A14A]" />
              <div>
                <p className="text-sm text-[#E8E4DE]/60">Joined</p>
                <p className="text-white">
                  {user?.created_at && new Date(user.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-[#C6A14A]" />
              <CardTitle className="text-lg text-white">Orders</CardTitle>
              <Badge variant="outline" className="ml-2 text-[#E8E4DE]/60">
                {orders.length}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <BillingSummary billing={billing} />
            {orders.length === 0 ? (
              <p className="text-center text-[#E8E4DE]/60 py-8">No orders found</p>
            ) : (
              <div className="space-y-2">
                {orders.map((order) => {
                  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
                  return (
                    <Link 
                      key={order.id}
                      to={order.service_type === 'real_estate' ? `/admin/real-estate-orders/${order.id}` : `/admin/orders/${order.id}`}
                      data-testid={`client-order-${order.id}`}
                      className="flex items-center justify-between p-3 bg-[#050816]/30 rounded-lg hover:bg-[#050816]/50 transition-colors group"
                    >
                      <div>
                        <p className="text-white group-hover:text-[#C6A14A] transition-colors">
                          {order.title || SERVICE_TYPE_LABELS[order.service_type] || order.service_type}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-[#E8E4DE]/40">
                            {SERVICE_TYPE_LABELS[order.service_type] || order.service_type}
                          </span>
                          <span className="text-xs text-[#E8E4DE]/20">•</span>
                          <span className="text-xs text-[#E8E4DE]/40">
                            {new Date(order.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        <OrderBillingBadges info={billing[order.id]} />
                        <Badge className={statusConfig.className}>
                          {statusConfig.label}
                        </Badge>
                        <ArrowRight className="h-4 w-4 text-[#C6A14A] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Role Change Confirmation Dialog */}
      <AlertDialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <AlertDialogContent className="bg-[#0f1724] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              {pendingRole === 'admin' ? (
                <>
                  <ShieldCheck className="h-5 w-5 text-[#C6A14A]" />
                  Grant Admin Access?
                </>
              ) : (
                <>
                  <ShieldOff className="h-5 w-5 text-red-400" />
                  Remove Admin Access?
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#E8E4DE]/60">
              {pendingRole === 'admin' ? (
                <>
                  <p>This will grant <span className="text-white font-medium">{user?.email}</span> full admin privileges.</p>
                  <p className="mt-2">They will be able to:</p>
                  <ul className="list-disc list-inside mt-1 text-sm">
                    <li>Manage all orders and users</li>
                    <li>Change user roles</li>
                    <li>Access admin dashboard</li>
                  </ul>
                </>
              ) : (
                <>
                  <p>This will remove admin privileges from <span className="text-white font-medium">{user?.email}</span>.</p>
                  <p className="mt-2">They will become a standard user and lose access to admin features.</p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-transparent border-white/10 text-[#E8E4DE] hover:bg-white/5 hover:text-white"
              data-testid="cancel-role-change-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRoleChange}
              className={pendingRole === 'admin' 
                ? "bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                : "bg-red-600 text-white hover:bg-red-700"
              }
              data-testid="confirm-role-change-btn"
            >
              {pendingRole === 'admin' ? (
                <>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Yes, Make Admin
                </>
              ) : (
                <>
                  <ShieldOff className="mr-2 h-4 w-4" />
                  Yes, Remove Admin
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
